// src/pages/SelectionOrders/components/tables/OrderHistoryTab.tsx
import React, { useEffect, useMemo, useState } from 'react';
import DefaultCard from '../../../../components/default/DefaultCard';
import LazyLoading from '../../../../components/LazyLoading';
import { ServiceResponse } from '../../../../app/services/shared/interfaces';
import { SelectionOrderHistoryRow } from '../../../../app/models/SelectionOrder';
import useSelectionOrderService from '../../../../app/services/hooks/useSelectionOrderService';
import { toast } from 'react-toastify';

type Props = {
  orderId: number;
};

const OrderHistoryTab: React.FC<Props> = ({ orderId }) => {
  const { getSelectionOrderHistory, fetchingGetOrderHistory } = useSelectionOrderService();
  const [rows, setRows] = useState<SelectionOrderHistoryRow[]>([]);
  const [page, setPage] = useState<number>(1);
  const [pageSize] = useState<number>(15);
  const [total, setTotal] = useState<number>(0);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(total / pageSize)),
    [total, pageSize]
  );

  const load = (p = 1) => {
    getSelectionOrderHistory(orderId, { page: p, page_size: pageSize }, {
      onSuccess: (resp: ServiceResponse) => {
        // Espera un payload: { results: [...], count: N }
        setRows(resp.data?.results ?? resp.data ?? []); // tolerante si no hay paginación
        setTotal(resp.data?.count ?? (resp.data?.results?.length ?? 0));
      },
      onError: (resp: ServiceResponse) => {
        toast.error(resp.message || 'No se pudo cargar el historial.');
      }
    });
  };

  useEffect(() => { load(1); setPage(1); }, [orderId]); // recarga al cambiar orden

  const renderDetalle = (r: SelectionOrderHistoryRow) => {
    switch (r.event_type) {
      case 'STATUS_CHANGED':
        return `Estado: ${r.from_status_label ?? r.from_status ?? '-'} → ${r.to_status_label ?? r.to_status ?? '-'}`;
      case 'BATCH_ADDED':
        return `Lote agregado: #${r.related_batch_id}`;
      case 'BATCH_REMOVED':
        return `Lote eliminado: #${r.related_batch_id}`;
      case 'USER_ADDED':
        return `Usuario agregado: #${r.related_user_id}`;
      case 'USER_REMOVED':
        return `Usuario removido: #${r.related_user_id}`;
      default:
        return r.extra || '';
    }
  };

  if (fetchingGetOrderHistory && rows.length === 0) {
    return (
      <DefaultCard>
        <LazyLoading height={200} />
      </DefaultCard>
    );
  }

  return (
    <DefaultCard>
      <div className="table-responsive">
        <table className="table table-hover align-middle">
          <thead>
            <tr>
              <th style={{ width: 180 }}>Fecha</th>
              <th style={{ width: 200 }}>Evento</th>
              <th>Detalle</th>
              <th style={{ width: 220 }}>Actor</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={4} className="text-muted">Sin eventos registrados.</td></tr>
            )}
            {rows.map(r => (
              <tr key={r.id}>
                <td>{new Date(r.created_at).toLocaleString()}</td>
                <td>{r.event_type_label ?? r.event_type}</td>
                <td>{renderDetalle(r)}</td>
                <td>{r.actor_name ?? (r.actor_id ? `#${r.actor_id}` : '-')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Paginación simple */}
      {totalPages > 1 && (
        <div className="d-flex justify-content-between align-items-center mt-2">
          <div className="text-muted">Página {page} de {totalPages}</div>
          <div className="btn-group">
            <button
              className="btn btn-outline-secondary btn-sm"
              disabled={page <= 1}
              onClick={() => { const p = page - 1; setPage(p); load(p); }}
            >
              « Anterior
            </button>
            <button
              className="btn btn-outline-secondary btn-sm"
              disabled={page >= totalPages}
              onClick={() => { const p = page + 1; setPage(p); load(p); }}
            >
              Siguiente »
            </button>
          </div>
        </div>
      )}
    </DefaultCard>
  );
};

export default OrderHistoryTab;
